import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';

import Button from 'components/field/Button';
import EnumSelect from 'components/field/EnumSelect';
import Form from 'components/form/Form';
import FormItem from 'components/form/FormItem';
import Alert from 'components/global/Alert';
import Modal from 'components/global/Modal';
import Typography from 'components/global/Typography';
import { ModalActions, ModalContent, ModalTitle } from 'components/global/styled/Modal.styled';
import { updateDynamicListData } from 'components/list/dynamicListSlice';

import useAccessControl from 'hooks/useAccessControl';
import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import useModalRouter from 'hooks/useModalRouter';
import { usePrompt } from 'hooks/usePrompt';

import { REASONCONFIG } from 'services/consts';

import AppLogger from 'utils/AppLogger';
import { DYNAMICLISTS } from 'utils/constants';

import ReasonLanguageList from './ReasonLanguageList';

const ReasonConfigForm = NiceModal.create(({ id, modalName, setReasonConfig, reasonType: reasonTypeProp }) => {
	const schema = useMemo(
		() =>
			yup
				.object()
				.shape({
					reasonType: yup.string().nullable().required(),
					reasonLangList: yup.array().of(
						yup.object().shape({
							reasonLang: yup.string().nullable().required(),
							reason: yup.string().nullable().required()
						})
					)
				})
				.required(),
		[]
	);
	const modal = useModal();
	const { closeModal } = useModalRouter();
	const axiosPrivate = useAxiosPrivate();
	const { showError, showSuccess } = useAlert();
	const [isLoading, setIsLoading] = useState(false);
	const dispatch = useDispatch();

	const handleCloseModal = useCallback(() => {
		if (modalName) {
			closeModal();
		} else {
			modal.remove();
		}
	}, [modalName]);

	const formMethods = useForm({
		resolver: yupResolver(schema),
		defaultValues: {
			reasonLangList: [{ reason: '', reasonLang: null }],
			reasonType: reasonTypeProp ?? null
		}
	});

	const {
		handleSubmit,
		reset,
		setValue,
		watch,
		control,
		clearErrors,
		setError,
		formState: { isDirty, isSubmitSuccessful, isSubmitting }
	} = formMethods;

	usePrompt(isDirty && !isSubmitSuccessful && !isSubmitting);

	const fillFormInEdit = async (id) => {
		setIsLoading(true);
		try {
			const response = await axiosPrivate.post(REASONCONFIG.GET_REASON_CONFIG, {
				ID: id
			});
			const { data } = response;
			const reasonLangList = [];
			if (data.reasonEng) {
				reasonLangList.push({ reasonLang: 'engValue', reason: data.reasonEng });
			}
			if (data.reasonAr) {
				reasonLangList.push({ reasonLang: 'arabicValue', reason: data.reasonAr });
			}
			if (data.reasonTr) {
				reasonLangList.push({ reasonLang: 'trValue', reason: data.reasonTr });
			}
			if (reasonLangList.length === 0) {
				reasonLangList.push({ reasonLang: null, reason: '' });
			}
			reset({
				ID: data.ID ?? null,
				reasonType: data.reasonType ?? null,
				reasonLangList
			});
		} catch (error) {
			AppLogger.error(error);
			showError();
		}

		setIsLoading(false);
	};

	useEffect(() => {
		if (id) fillFormInEdit(id);
	}, [id]);

	const onSubmit = async (data) => {
		// row means we are in edit mode else is add new branch.

		setIsLoading(true);
		const URL = id ? REASONCONFIG.UPDATE_REASON_CONFIG : REASONCONFIG.ADD_REASON_CONFIG;

		const obj = {
			reasonAr: null,
			reasonEng: null,
			reasonTr: null
		};

		const bodyObj = data.reasonLangList.reduce((acc, reason) => {
			if (reason.reasonLang === 'arabicValue') {
				acc.reasonAr = reason.reason;
			}
			if (reason.reasonLang === 'engValue') {
				acc.reasonEng = reason.reason;
			}
			if (reason.reasonLang === 'trValue') {
				acc.reasonTr = reason.reason;
			}
			return acc;
		}, obj);

		try {
			const response = await axiosPrivate.post(URL, {
				ID: id,
				reasonType: data.reasonType,
				...bodyObj
			});
			const { status } = await response;
			if (status === 200) {
				setReasonConfig &&
					setReasonConfig(
						bodyObj.reasonEng
							? bodyObj.reasonEng
							: bodyObj.reasonAr
								? bodyObj.reasonAr
								: bodyObj.reasonTr
									? bodyObj.reasonTr
									: bodyObj.reasonEng
					);
				dispatch(updateDynamicListData(DYNAMICLISTS.REASON_CONFIG_LIST));
				showSuccess();
				handleCloseModal();
			} else {
				AppLogger.error(response);
			}
		} catch (error) {
			AppLogger.error(error);
			showError();
		}
		setIsLoading(false);
	};

	const LargeColSpan = {
		md: 12
	};

	const isAuthorized = useAccessControl();

	const [reasonTypes, setReasonTypes] = useState(['onHold', 'return', 'cancel', 'notAvailable']);

	useEffect(() => {
		const authorizedReasonTypes = [...reasonTypes];
		if (isAuthorized('RMSModule')) authorizedReasonTypes.push('customerReturnReason', 'undeliveredAttempt');
		if (isAuthorized('OTOFlexModule')) authorizedReasonTypes.push('returnOTOFlex');
		setReasonTypes(authorizedReasonTypes);
	}, []);

	return (
		<Modal fullwidth maxWidth="small" name={modalName} className="modal-reasonConfig">
			<ModalTitle>
				<Typography variant="p1">reasonForm</Typography>
			</ModalTitle>
			<ModalContent>
				<Form formMethods={formMethods}>
					<Alert content="reasonCongfig.description" className="m-4" showIcon />
					<FormItem colSpan={LargeColSpan} label="reasonType" required>
						<EnumSelect
							name="reasonType"
							disabled={reasonTypeProp}
							data={reasonTypes}
							setValue={(value) => {
								setValue('reasonType', value, { shouldValidate: true, shouldDirty: true });
							}}
							value={watch('reasonType')}
						/>
					</FormItem>
					<FormItem>
						<ReasonLanguageList
							isLoading={isLoading}
							control={control}
							clearErrors={clearErrors}
							watch={watch}
							schema={schema}
							setValue={setValue}
							setError={setError}
						/>
					</FormItem>
				</Form>
			</ModalContent>
			<ModalActions>
				<Button label="cancel" variant="outline" onClick={handleCloseModal} />
				<Button
					loading={isLoading}
					label={id ? 'save' : 'add'}
					disabled={!isDirty}
					disabledTooltip={!isDirty && 'formInfo.noChanges'}
					onClick={handleSubmit(onSubmit, (errors) => {
						AppLogger.log(errors);
					})}
					iconPos="right"
				/>
			</ModalActions>
		</Modal>
	);
});
export default ReasonConfigForm;
